import { Injectable, signal, effect, Injector, inject } from '@angular/core';

import { Client } from 'src/providers/client/client';
import { WindowService } from 'src/shared/providers/window.service';

export type ThemeMode = 'dark' | 'light';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'tidy_theme_mode';
  private readonly USER_SET_KEY = 'tidy_theme_user_set';
  private readonly FORCE_DARK_KEY = 'tidy_force_dark_mode';
  private injector = inject(Injector);
  private windowService = inject(WindowService);
  private isUserAction = false;

  readonly themeMode = signal<ThemeMode>(this.getInitialTheme());

  constructor() {
    if (localStorage.getItem(this.FORCE_DARK_KEY) === 'true') {
      document.body.classList.remove('dark-mode', 'light-mode');
      document.body.classList.add('dark-mode');
    } else {
      this.applyTheme(this.themeMode());
    }
    effect(() => {
      const mode = this.themeMode();
      if (localStorage.getItem(this.FORCE_DARK_KEY) !== 'true') {
        this.applyTheme(mode);
      }
      localStorage.setItem(this.STORAGE_KEY, mode);
      if (this.isUserAction) {
        this.isUserAction = false;
        this.saveToMetadata(mode);
      }
    });
  }

  forceDarkMode(): void {
    localStorage.setItem(this.FORCE_DARK_KEY, 'true');
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add('dark-mode');
  }

  clearForcedDarkMode(): void {
    if (localStorage.getItem(this.FORCE_DARK_KEY) !== 'true') {return;}
    localStorage.removeItem(this.FORCE_DARK_KEY);
    this.applyTheme(this.themeMode());
  }

  isDarkMode(): boolean {
    // Check forced state (not in signal) for pages like getting-started that override visuals
    return this.themeMode() === 'dark' || localStorage.getItem(this.FORCE_DARK_KEY) === 'true';
  }

  toggleTheme(): void {
    this.isUserAction = true;
    localStorage.setItem(this.USER_SET_KEY, 'true');
    this.themeMode.set(this.isDarkMode() ? 'light' : 'dark');
  }

  setTheme(mode: ThemeMode): void {
    this.themeMode.set(mode);
    this.applyTheme(mode);
    localStorage.setItem(this.STORAGE_KEY, mode);
  }

  private applyTheme(mode: ThemeMode): void {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(`${mode}-mode`);
  }

  private getInitialTheme(): ThemeMode {
    // User explicitly chose a theme — always respect it
    if (localStorage.getItem(this.USER_SET_KEY) === 'true') {
      return (localStorage.getItem(this.STORAGE_KEY) as ThemeMode) || 'dark';
    }

    // Platform default: desktop → dark, mobile → follow the OS (system) preference
    if (this.windowService.isDesktopRes) {
      return 'dark';
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  }

  private saveToMetadata(mode: ThemeMode): void {
    try {
      const client = this.injector.get(Client);
      client.addMetadata({ key: 'theme_mode', value: mode });
    } catch (e) {
      // Client may not be available if not logged in
    }
  }
}
